import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SearchService } from './search.service';
import { FilterService } from './filter.service';
import { UiStateService } from './ui-state.service';
import { ScrollPositionService } from './scroll-position.service';
import { clearReceiverListRestoreState, leaveReceiverPersonaListState } from '../receiver/receiver-list-state.storage';
import {
  clearGatehouseListRestoreState,
  leaveGatehousePersonaListState
} from '../gatehouse/gatehouse-list-state.storage';
import { clearShuntingListRestoreState, leaveShuntingPersonaListState } from '../shunting/shunting-list-state.storage';
import { clearAuditListRestoreState, leaveAuditPersonaListState } from '../audit/audit-list-state.storage';
import { clearGrowersListRestoreState, leaveGrowersPersonaListState } from '../growers/growers-list-state.storage';
import {
  clearTransloadingListRestoreState,
  leaveTransloadingPersonaListState
} from '../transloading/transloading-list-state.storage';
import { clearSchedulerListRestoreState, leaveSchedulerPersonaListState } from '../scheduler/scheduler-list-state.storage';

/**
 * Service for handling persona-to-route mapping and navigation
 * Eliminates code duplication across all persona components
 */
@Injectable({
  providedIn: 'root'
})
export class PersonaRoutingService {
  private readonly searchService = inject(SearchService);
  private readonly filterService = inject(FilterService);
  private readonly uiState = inject(UiStateService);
  private readonly scrollPosition = inject(ScrollPositionService);
  private lastPersonaRoute: string | null = null;

  /** Persona list routes (not profile/contacts). Kept in one place for leave/sync logic. */
  private readonly personaListRoutes: readonly string[] = [
    '/growers',
    '/transloading',
    '/receiver',
    '/shunting',
    '/scheduler',
    '/gatehouse',
    '/audit'
  ];

  constructor(private readonly router: Router) {}

  /**
   * Sets {@link #lastPersonaRoute} from the current browser URL when it is a persona list route.
   * Ensures navbar navigation (e.g. Profile from Audit) runs the correct `leave*` handler when
   * the user reached the persona via direct URL (e.g. Profile → Audit) instead of Home.
   */
  syncLastPersonaRouteFromRouter(): void {
    const path = this.router.url.split('?')[0];
    if (this.personaListRoutes.includes(path)) {
      this.lastPersonaRoute = path;
    }
  }

  /**
   * Maps a persona string to its corresponding route
   * @param persona The persona name (case-insensitive)
   * @returns The route path for the persona
   */
  personaToRoute(persona: string): string {
    const p = (persona || '').toLowerCase();
    
    // PersonaID: 1=Admin, 2=Grower, 3=Transloader, 4=Receiver, 5=Shunter/Shunting, 6=Scheduler, 7=Gatehouse
    const exactMatchMap: Record<string, string> = {
      'admin': '/profile',
      'grower': '/growers',
      'growers': '/growers',
      'transloader': '/transloading',
      'transloading': '/transloading',
      'receiver': '/receiver',
      'shunter': '/shunting',
      'shunting': '/shunting',
      'scheduler': '/scheduler',
      'gate house': '/gatehouse',
      'gatehouse': '/gatehouse',
      'audit': '/audit',
      'auditor': '/audit'
    };
    
    // Check for exact match first
    if (p in exactMatchMap) {
      return exactMatchMap[p];
    }
    
    // Fallback to partial matching for composite personas (e.g., 'Grower Admin')
    if (p.includes('grower')) return '/growers';
    if (p.includes('transload')) return '/transloading';
    if (p.includes('receiv')) return '/receiver';
    if (p.includes('shunting') || p.includes('shunt')) return '/shunting';
    if (p.includes('schedule')) return '/scheduler';
    if (p.includes('gate')) return '/gatehouse';
    if (p.includes('audit')) return '/audit';
    if (p.includes('admin')) return '/profile';
    
    // Default fallback
    return '/profile';
  }

  /**
   * Clear search and filters when navigating to a different persona
   * @param targetRoute The route being navigated to
   */
  private clearSearchAndFiltersIfPersonaChanged(targetRoute: string): void {
    this.syncLastPersonaRouteFromRouter();

    // Check if we're navigating to a different persona
    const isPersonaRoute = this.personaListRoutes.includes(targetRoute);
    const isChangingPersona = isPersonaRoute && this.lastPersonaRoute !== null && this.lastPersonaRoute !== targetRoute;
    
    if (isChangingPersona) {
      console.log('🔄 Persona changed - clearing search and filters');
      if (this.lastPersonaRoute === '/receiver') {
        leaveReceiverPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/shunting') {
        leaveShuntingPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/gatehouse') {
        leaveGatehousePersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/audit') {
        leaveAuditPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/growers') {
        leaveGrowersPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/transloading') {
        leaveTransloadingPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/scheduler') {
        leaveSchedulerPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else {
        clearReceiverListRestoreState();
        clearShuntingListRestoreState();
        clearGatehouseListRestoreState();
        clearAuditListRestoreState();
        clearGrowersListRestoreState();
        clearTransloadingListRestoreState();
        clearSchedulerListRestoreState();
        this.searchService.clear();
        this.filterService.clear();
        this.uiState.setSearchOpen(false);
        this.uiState.setFiltersOpen(false);
      }
    }
    
    // Update last persona route
    if (isPersonaRoute) {
      this.lastPersonaRoute = targetRoute;
    }
  }

  /**
   * Handle navigation based on nav ID (profile, home, contacts)
   * @param navId The navigation identifier
   * @param currentPersona The current persona to determine home route
   */
  handleNavigation(navId: string, currentPersona: string): void {
    this.syncLastPersonaRouteFromRouter();
    const id = (navId || '').toLowerCase();
    
    const routeMap: Record<string, string> = {
      'profile': '/profile',
      'contacts': '/contacts'
    };

    // If it's a known route, use it
    if (id in routeMap) {
      if (this.lastPersonaRoute === '/receiver') {
        leaveReceiverPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/shunting') {
        leaveShuntingPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/gatehouse') {
        leaveGatehousePersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/audit') {
        leaveAuditPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/growers') {
        leaveGrowersPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/transloading') {
        leaveTransloadingPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      } else if (this.lastPersonaRoute === '/scheduler') {
        leaveSchedulerPersonaListState({
          filterService: this.filterService,
          searchService: this.searchService,
          ui: this.uiState,
          scrollPosition: this.scrollPosition
        });
      }
      this.router.navigateByUrl(routeMap[id]);
      return;
    }

    // If it's 'home', navigate to the persona's home route
    if (id === 'home') {
      const homeRoute = this.personaToRoute(currentPersona);
      this.clearSearchAndFiltersIfPersonaChanged(homeRoute);
      this.router.navigateByUrl(homeRoute);
      return;
    }

    // Default: navigate to current persona's home
    const defaultRoute = this.personaToRoute(currentPersona);
    this.clearSearchAndFiltersIfPersonaChanged(defaultRoute);
    this.router.navigateByUrl(defaultRoute);
  }
}

